import SEO from "@/components/SEO";
import {
  Blockquote,
  CTAButton,
  H2,
  HeroH1,
  InlineLink,
  P,
  SeoPageShell,
} from "./seo/SeoPageLayout";

const EmotionalInvalidationExamples = () => {
  return (
    <SeoPageShell>
      <SEO
        title="Emotional Invalidation Examples: 'You're Overreacting' and Other Erasers"
        description="Emotional invalidation is when your feelings are treated as the problem instead of the thing you're feeling. Real examples of the phrases that quietly erase your reality."
        path="/emotional-invalidation-examples"
      />

      <HeroH1>Emotional Invalidation Examples: When Your Feelings Become the Problem</HeroH1>

      <P>
        You react to something. Maybe you say it hurt. Maybe your voice tightens. And instead of
        the thing you're reacting to being addressed, your reaction itself becomes the topic.
        You're now defending your right to feel what you're feeling, while the original thing
        quietly disappears from the conversation.
      </P>
      <P>
        Over time, you stop bringing things up. Which is, conveniently, the point. It is part of{" "}
        <InlineLink to="/why-do-i-feel-exhausted-after-conversations">
          why these conversations leave you so exhausted →
        </InlineLink>
      </P>

      <H2>The phrases that erase you</H2>
      <P>These are the moves. Each one says, in different words, "your feeling is the problem."</P>

      <Blockquote>"You're overreacting."</Blockquote>
      <Blockquote>"That's not what happened."</Blockquote>
      <Blockquote>"You're too sensitive."</Blockquote>
      <Blockquote>"I was just joking. Why do you take everything so seriously?"</Blockquote>
      <Blockquote>"Why are you making this a big deal?"</Blockquote>
      <Blockquote>"You always do this."</Blockquote>
      <Blockquote>"I don't know why you're upset. Nothing happened."</Blockquote>

      <P>
        None of these engage with what you actually said. Every one of them moves the
        conversation onto the meta-topic of your reaction.
      </P>

      <H2>Why it works</H2>
      <P>
        Invalidation outsources your reality-check to the other person. You stop asking "was
        that okay?" and start asking "am I okay for noticing it wasn't okay?" Over time, you
        stop trusting your own read of events. That loss of self-trust is the actual cost — not
        the individual moment, but the slow accumulation of being told, in small ways, that what
        you noticed didn't happen.
      </P>

      <CTAButton />

      <H2>Invalidation vs. a genuine disagreement</H2>
      <P>
        A real disagreement engages with your feeling. It might say "I hear that hurt, and I see
        it differently because…" — there is contact with what you said.
      </P>
      <P>
        Invalidation disputes your right to have the feeling at all. It does not argue about
        what happened. It argues about whether you are allowed to be having a reaction. That is
        the clearest tell.
      </P>

      <H2>Getting your reality back</H2>
      <P>
        Naming each move as it happens is what restores the ground under your feet. "That was an
        invalidation." "That was a reframe of my reaction as the issue." You don't need to win
        the argument. You only need to stop accepting the frame that your reaction is the
        problem instead of the thing you were reacting to.
      </P>
      <P>
        Invalidation often escalates into a full blame-flip — your reaction stops being merely
        unreasonable and becomes the offense itself.{" "}
        <InlineLink to="/darvo-examples">
          See how that pattern works (DARVO examples) →
        </InlineLink>{" "}
        Surfacing patterns like these is{" "}
        <InlineLink to="/articulation-before-adjudication">
          the emerging category these tools belong to →
        </InlineLink>
      </P>

      <CTAButton />
    </SeoPageShell>
  );
};

export default EmotionalInvalidationExamples;
